import { Box, Group, SimpleGrid, Text } from '@mantine/core';

import { BASE_LABELS } from '../../constants/base';
import { analyticsTypes } from '../../types';
import {
  formatMoney,
  formatNumber,
  formatNullableCount,
  formatPeriodCaption,
  toPeriodDates,
} from '../../utils/base';

import { KpiCard } from './KpiCard';

type KpiSectionsProps = {
  report: analyticsTypes.BaseAnalyticsReport;
  isDialogsAvailable: boolean;
};

export const KpiSections = ({ report, isDialogsAvailable }: KpiSectionsProps) => {
  const currency = report.currency;
  const periodCaption = formatPeriodCaption(...toPeriodDates(report.period));

  return (
    <Box>
      <SectionHeader title={BASE_LABELS.SUMMARY} caption={periodCaption} />
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 5 }} spacing="md" mb="xl">
        <KpiCard
          title={BASE_LABELS.TOTAL_REVENUE}
          value={formatMoney(report.summary.totalRevenue, currency)}
          hint={BASE_LABELS.TOTAL_REVENUE_HINT}
        />
        <KpiCard
          title={BASE_LABELS.BASE_REVENUE}
          value={formatMoney(report.summary.baseRevenue, currency)}
          hint={BASE_LABELS.BASE_REVENUE_HINT}
          isHero
        />
        <KpiCard
          title={BASE_LABELS.FIRST_LINE_REVENUE}
          value={formatMoney(report.summary.firstLineRevenue, currency)}
          hint={BASE_LABELS.FIRST_LINE_REVENUE_HINT}
        />
        <KpiCard
          title={BASE_LABELS.TOTAL_CHECKS}
          value={formatNumber(report.summary.totalChecks)}
          hint={BASE_LABELS.TOTAL_CHECKS_HINT}
        />
        <KpiCard
          title={BASE_LABELS.AVERAGE_CHECK}
          value={formatMoney(report.summary.averageCheck, currency)}
          hint={BASE_LABELS.AVERAGE_CHECK_HINT}
        />
      </SimpleGrid>

      <SectionHeader title={BASE_LABELS.KEY_METRICS} caption={periodCaption} />
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 5 }} spacing="md">
        <KpiCard
          title={BASE_LABELS.ACTIVE_DIALOGS}
          value={formatNullableCount(report.keyMetrics.activeDialogs)}
          hint={
            isDialogsAvailable
              ? BASE_LABELS.ACTIVE_DIALOGS_HINT
              : BASE_LABELS.ACTIVE_DIALOGS_UNAVAILABLE
          }
          isHero
        />
        <KpiCard
          title={BASE_LABELS.BASE_REVENUE}
          value={formatMoney(report.keyMetrics.baseRevenue, currency)}
          hint={BASE_LABELS.BASE_REVENUE_HINT}
        />
        <KpiCard
          title={BASE_LABELS.BASE_CHECKS}
          value={formatNumber(report.keyMetrics.baseChecks)}
          hint={BASE_LABELS.BASE_CHECKS_HINT}
        />
        <KpiCard
          title={BASE_LABELS.AVERAGE_CHECK}
          value={formatMoney(report.keyMetrics.averageCheck, currency)}
          hint={BASE_LABELS.BASE_AVERAGE_HINT}
        />
        <KpiCard
          title={BASE_LABELS.LEADS_TO_BASE}
          value={formatNumber(report.keyMetrics.leadsToBase)}
          hint={BASE_LABELS.LEADS_TO_BASE_HINT}
        />
      </SimpleGrid>
    </Box>
  );
};

const SectionHeader = ({ title, caption }: { title: string; caption: string }) => (
  <Group justify="space-between" mb="sm">
    <Text fw={600}>{title}</Text>
    <Text size="sm" c="dimmed">
      {caption}
    </Text>
  </Group>
);
